import type { DayPickerProps, Matcher } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';

import { useMemo } from 'react';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import { cn, focusRing } from '../../lib/utils.ts';

const defaultClassNames = {
    months: 'flex flex-row space-y-0 relative',
    month: 'space-y-4 p-3',
    nav: 'absolute flex justify-between top-0 left-0 w-full px-4 py-3 z-10',
    month_caption: 'flex justify-center pt-1 relative items-center',
    caption_label: 'text-sm font-medium text-gray-900 dark:text-gray-50',
    table: 'w-full border-collapse space-y-1',
    weekdays: '[&>th]:font-medium [&>th]:text-sm text-neutral-400',
    head_cell: 'w-9 font-medium text-sm sm:text-sm text-center text-gray-400 dark:text-gray-600 pb-2',
    day: cn('relative p-0 text-center focus-within:relative', 'text-gray-900 dark:text-gray-50'),
    chevron: 'border border-gray-300  h-7 w-7 p-1.5 rounded-sm ',
    day_button: cn(
        'size-9 rounded-sm text-sm focus:z-10',
        'text-gray-900 dark:text-gray-50 ',
        'hover:bg-blue-200',
        'disabled:text-neutral-300',
        '&[aria-selected="true"]>:text-white',
        focusRing,
    ),

    today: 'font-semibold',
    selected: 'rounded-sm text-white aria-selected:bg-blue-500',
    outside: 'text-gray-400 dark:text-gray-600',
    range_middle: cn(
        'rounded-none!',
        'aria-selected:bg-blue-50! aria-selected:text-neutral-800! [&>button]:text-neutral-500 ',
    ),
    range_start: 'rounded-r-none rounded-l! [&>button]:text-white',
    range_end: 'rounded-l-none rounded-r! [&>button]:text-white',
    hidden: 'invisible',
};

export type CalendarProps = DayPickerProps & {
    enableYearNavigation?: boolean;
    numberOfMonths?: number;
    className?: string;
    classNames?: Record<string, string>;
};

const Calendar = ({
    enableYearNavigation,
    numberOfMonths = 1,
    className,
    classNames,
    ...rest
}: CalendarProps) => {
    const { localeData } = useLocale();

    const combinedClassNames = useMemo(
        () => (classNames ? { ...defaultClassNames, ...classNames } : defaultClassNames),
        [classNames],
    );

    return (
        <DayPicker
            numberOfMonths={numberOfMonths}
            locale={localeData}
            weekStartsOn={1}
            showOutsideDays={numberOfMonths === 1}
            className={cn(className)}
            classNames={combinedClassNames}
            {...rest}
        />
    );
};

export { Calendar, type Matcher };
